import React, { useMemo } from 'react';
import {
  Box,
  Button,
  MenuItem,
  MenuList,
  Paper,
  Typography,
  Collapse,
  CircularProgress,
} from '@mui/material';
import { ExpandMore, ExpandLess } from '@mui/icons-material';
import { PyrenzModal, PyrenzModalContent } from '@client/theme';
import { useMenuAPI, Character } from '@components';
import * as MenuComponents from './items';

interface MenuProps {
  onClose: () => void;
  char: Character;
}

type MenuItemProps = {
  customization?: any;
  subscriptionPlan?: string;
  char?: Character;
  onSubmit?: (details: any) => Promise<void>;
};

const menuConfig = [
  {
    id: 'cosmetic',
    title: 'Cosmetic',
    component: 'Cosmetic',
    defaultOpen: true,
  },
  {
    id: 'customization',
    title: 'AI Customization',
    component: 'Customization',
  },
  {
    id: 'characterDetails',
    title: 'Character Details',
    component: 'CharacterDetails',
    centered: true,
  },
  { id: 'memory', title: 'Memory', component: 'Memory', centered: true },
  { id: 'modelControl', title: 'Model Control', component: 'ModelControl' },
  { id: 'persona', title: 'Persona', component: 'Persona' },
  { id: 'jailbreak', title: 'Jailbreak', component: 'Jailbreak' },
  {
    id: 'chatHistory',
    title: 'Chat History',
    component: 'ChatHistory',
    centered: true,
    spot: 'center',
  },
];

export function Menu({ onClose, char }: MenuProps) {
  const {
    isDropdownOpen,
    setIsDropdownOpen,
    selectedOption,
    setSelectedOption,
    loading,
    handleCharacterDetailsSubmit,
    aiCustomization,
    subscriptionPlan,
  } = useMenuAPI({ char });

  const section = menuConfig.find((s) => s.title === selectedOption);

  const renderContent = useMemo(() => {
    if (!section) return null;
    const Component = (
      MenuComponents as Record<string, React.ComponentType<MenuItemProps>>
    )[section.component];
    if (!Component) return <Typography>Component not found</Typography>;

    return (
      <Component
        customization={aiCustomization}
        subscriptionPlan={subscriptionPlan}
        char={char}
        onSubmit={handleCharacterDetailsSubmit}
      />
    );
  }, [
    section,
    aiCustomization,
    subscriptionPlan,
    char,
    handleCharacterDetailsSubmit,
  ]);

  const menuOptions = menuConfig.map((s) => s.title);

  return (
    <PyrenzModal open={true} onClose={onClose}>
      <PyrenzModalContent>
        {loading ? (
          <Box
            display="flex"
            justifyContent="center"
            alignItems="center"
            height="100%"
          >
            <CircularProgress />
          </Box>
        ) : (
          <>
            <Box display="flex" flexDirection="column" mb={2}>
              <Button
                onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                variant="contained"
                color="primary"
                fullWidth
                endIcon={isDropdownOpen ? <ExpandLess /> : <ExpandMore />}
                sx={{
                  backgroundColor: 'rgba(74, 85, 104, 0.7)',
                  '&:hover': { backgroundColor: 'rgba(113, 128, 150, 0.8)' },
                  justifyContent: 'space-between',
                }}
              >
                <Typography>{selectedOption}</Typography>
              </Button>
              <Collapse in={isDropdownOpen}>
                <Paper
                  elevation={3}
                  sx={{
                    mt: 2,
                    borderRadius: '8px',
                    overflow: 'hidden',
                    backgroundColor: 'rgba(255,255,255,0.05)',
                  }}
                >
                  <MenuList>
                    {menuOptions.map((option) => (
                      <MenuItem
                        key={option}
                        onClick={() => {
                          setSelectedOption(option);
                          setIsDropdownOpen(false);
                        }}
                        sx={{
                          color: '#fff',
                          '&:hover': {
                            backgroundColor: 'rgba(255, 255, 255, 0.08)',
                          },
                        }}
                      >
                        {option}
                      </MenuItem>
                    ))}
                  </MenuList>
                </Paper>
              </Collapse>
            </Box>
            <Box
              display="flex"
              justifyContent="center"
              alignItems="center"
              width="100%"
            >
              {renderContent}
            </Box>
          </>
        )}
      </PyrenzModalContent>
    </PyrenzModal>
  );
}
